/**
 * Where a request row leads: a submission awaiting review opens its latest
 * review, everything else its intake page (the builder, or tracking once
 * sent). Shared by the nav tree and the Requests list.
 */
import { useCallback } from "react";

import type { OutgoingIntake } from "@/api/types/outgoingIntake";

import { useRequestsIde } from "./context/RequestsIdeContext";
import { useRequestsData } from "./data/RequestsDataContext";
import { intakeArtifact } from "./model";
import { submissionArtifactId } from "./types";

export const useOpenRequest = () => {
  const { open } = useRequestsIde();
  const data = useRequestsData();

  const openReview = useCallback(
    async (intake: OutgoingIntake) => {
      const detail = await data.loadDetail(intake.outgoing_intake_id);
      const latest = detail?.submissions
        .slice()
        .sort((a, b) => b.revision_number - a.revision_number)[0];
      if (!latest) {
        open(intakeArtifact(intake));
        return;
      }
      open({
        kind: "submission",
        id: submissionArtifactId(
          intake.outgoing_intake_id,
          latest.outgoing_intake_submission_id
        ),
        title: `${intake.title} · Submission #${latest.revision_number}`
      });
    },
    [data, open]
  );

  const openRequest = useCallback(
    (intake: OutgoingIntake) => {
      if (intake.status === "awaiting_review") void openReview(intake);
      else open(intakeArtifact(intake));
    },
    [open, openReview]
  );

  return { openRequest, openReview };
};
